/**
 * @format
 * @module bootstraps/main
 * @description The main bootstrap for majority of site pages
 */
import domReady from "domready";
import isNil from "lodash/isNil";

import audienceBootstrap, { installLastAccessBeacon } from "lib/audience/bootstrap";
import { adSequencingReadyResolve } from "lib/commercial/ad-sequencing";
import commercialBootstrap from "lib/commercial/bootstrap";
import ee from "lib/eventemitter";
import lazyImagesInit from "lib/lazy-images";
import websiteAlerts from "lib/notifications/website-alerts";
import { mark, measure } from "lib/performance/user-timing";
import datalayerReady from "lib/audience/datalayer/ready";
import adSequencingReady from "lib/commercial/ad-sequencing/ready";
import yourGlobeOverlay from "lib/your-globe/overlay";

import lazyBgImagesInit from "ui/lazy-bg-images";
import { liveSuggestInit } from "ui/live-suggest";
import { siteHeaderInit } from "ui/site-header";
import userStatesInit from "ui/user-states";

import { hungryForCookies } from "utils/cookie-monster";
import crow from "utils/crow";
import { getUrlVars } from "utils/urls";
import { isFunctionThenInvoke, windowOnLoad } from "utils/utils";
import { register as registerWindowEvents } from "utils/window-events";

const { eatcookies } = getUrlVars();

// the promise will only be fulfilled after document ready
const domReadyPromise = new Promise(resolve => {
  domReady(resolve);
});

const boot = crow.wrap(
  {
    feature: "bootstraps : main"
  },
  init
);

/**
 * @method appReady
 */
function appReady() {
  ee.trigger("app:ready");
  ee.removeEvent("app:ready");
  if (Array.isArray(window.tgam.appReady)) {
    window.tgam.appReady.forEach(isFunctionThenInvoke);
  }
}

/**
 * @method sesameStreet
 * @description Determine if/when to eat cookies.
 */
function sesameStreet() {
  window.requestIdleCallback(() => {
    const now = new Date();
    const isHungry = isNil(eatcookies) ? hungryForCookies(now) : eatcookies === true;
    if (isHungry) {
      import("utils/cookie-monster" /* webpackChunkName: "sesame-street" */)
        .then(monster => {
          monster.eatTheCookies(now);
        })
        .catch(console.error);
    }
  });
}

/**
 * @method loadDeferred
 * @description Load deferred methods
 */
function loadDeferred() {
  import("deferred/main" /* webpackChunkName: "deferred/main" */)
    .then(m => {
      m.default();
      // Notify Analytics that the page has "officially" loaded (an approximate signal)
      window.analyticsLayer.push({ event: "pageLoaded" });
    })
    .catch(console.error);
}

const subscriptionSiteOrigins = [
  "https://sec.theglobeandmail.com",
  "https://sec.stg.theglobeandmail.com",
  "https://sec.dev.theglobeandmail.com"
];

/**
 * @method registerPostMessageEvent
 * @description Refresh the page after a `postMessage` event is dispatched from the subscription site
 */
function registerPostMessageEvent() {
  // After a successful login, the modal dialog `iframe` dispatches a `postMessage` event
  window.addEventListener("message", event => {
    if (!subscriptionSiteOrigins.includes(event.origin)) {
      return;
    }
    if (event.data === "reload") {
      window.location.reload();
    }
  });
}

/**
 * @method init
 * @description Turn the website on.
 */
function init() {
  mark("default_bootstrap_started");
  registerPostMessageEvent();
  registerWindowEvents();
  mark("ui_states_started");
  lazyBgImagesInit();
  lazyImagesInit();
  mark("ui_states_done");
  measure("ui_states");

  // DFP ads must wait for adSequencing APIs (when that functionality is enabled)
  // defer the execution of darwin tests until both adSequencing and the datalayer are ready
  Promise.all([adSequencingReady, datalayerReady]).then(() => {
    installLastAccessBeacon(); // important TGAM analytic
    commercialBootstrap(); // Depends on Datalayer also being ready
    mark("user_states_started");
    userStatesInit();
    mark("user_states_done");
    measure("user_states");
    websiteAlerts();
    sesameStreet();
    yourGlobeOverlay(); // Initialize the Your Globe overlay
    windowOnLoad(loadDeferred);
  });

  // dataScienceAdSequencing(); - Commented out until actually used!
  adSequencingReadyResolve(); // This would normally occur in above method.

  audienceBootstrap(); // marks handled within
  siteHeaderInit(); // Initialize the site header when clicked
  liveSuggestInit(); // Initialize the financial and header search when focused

  mark("default_bootstrap_done");
  measure("default_bootstrap");

  if (/webvitals=true/.test(window.location.search)) {
    import("web-vitals" /* webpackChunkName: "web-vitals" */).then(m => {
      m.getCLS(console.info);
      m.getLCP(console.info);
      m.getFID(console.info);
    });
  }
}

// On document ready, bootstrap the app.
// Anything required in the bootstrap's dependency graph
// can therefore expect that document ready is complete/interactive
// so modules do not have to implement it themselves
domReadyPromise
  .then(boot)
  .then(appReady)
  .catch(e => {
    console.error(e);
    throw e; // re-throw the error
  });
